import React, { useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { PillBottle, AlertTriangle, Info, X, Search } from 'lucide-react';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { computeDuplicateGroups, computeSameSubstanceGroups } from '../../utils/medicationDuplicates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Vérificateur de doublons — DEUX niveaux d'alerte distincts, pas un
// vérificateur de principe actif au sens large ni un vérificateur complet
// d'interactions médicamenteuses. Branché sur GET /api/medications
// (registre pharmaceutique officiel importé de PharmNet, voir
// backend/routes/medications.py) — remplace l'ancienne petite liste
// statique d'une quarantaine de médicaments.
//
// NIVEAU 1 (rouge, `duplicateGroups`) — doublon EXACT, meme `dci_code`.
// IMPORTANT — comportement reel de `dci_code`, verifie en testant en
// production (2026-09-05, requetes reseau captures sur tabbibak.com) : ce
// n'est PAS un code de principe actif pur. Il regroupe de façon fiable
// plusieurs MARQUES d'un même produit à dosage/forme identiques (verifie :
// AMOXICILLINE-BAYER / AMOXICILLINE EG / AMOXICILLINE-RATIOPHARM, toutes
// 250MG/5ML, partagent le même dci_code) — mais PAS les différents dosages
// d'un même médicament (verifie : les 16 variantes DOLIPRANE/CODOLIPRANE ont
// chacune un dci_code different, y compris DOLIPRANE 500MG vs DOLIPRANE
// 1000MG).
//
// NIVEAU 2 (ambre, `sameSubstanceGroups`, ajoute le 2026-09-05) — meme
// SUBSTANCE (`dci_name`, apres normalisation legere, voir
// utils/medicationDuplicates.js) a des `dci_code` differents (donc
// dosage/forme differents, ex. Doliprane 500 + Doliprane 1000).
// Deliberement moins alarmiste que le niveau 1 (pas "surdosage" mais
// "verifiez la dose totale") : associer deux dosages differents d'une
// meme substance peut etre volontaire selon la prescription (schema de
// charge, ajustement posologique...), ce n'est pas automatiquement une
// erreur contrairement a un vrai doublon exact. Rapprochement fait
// UNIQUEMENT sur egalite stricte du libelle `dci_name` normalise (pas de
// correspondance floue) : deux formulations de la meme molecule sous des
// libelles differents (ex. "AMOXICILLINE SODIQUE" forme injectable vs
// "AMOXICILLINE SOUS FORME TRIHYDRATEE" forme orale — sels et voies
// d'administration differents) ne sont PAS rapprochees ici, deliberement :
// mieux vaut rater certains cas que fusionner a tort des formulations
// dont l'equivalence de dose n'est pas etablie sans jugement pharmaceutique.
//
// Cette meme logique (deux niveaux + normalisation) est reutilisee telle
// quelle dans pages/DoctorDashboard.js (verification a la redaction d'une
// ordonnance) via utils/medicationDuplicates.js, pour que patient et
// medecin voient exactement le meme comportement.
//
// Nom de substance lisible (`dci_name`) : le registre PharmNet lui-meme
// n'expose aucun libelle texte du principe actif (verifie par sonde directe
// sur la table source, voir
// backend/tools/pharmnet_dictionary/probe_dci_name.py — 38 champs au
// total, aucun candidat). `/api/medications` l'ajoute desormais cote
// backend par une jointure en memoire sur un fichier bundle avec le depot
// (backend/data/dci_names.json), construit a partir de la Nomenclature
// Nationale des Produits Pharmaceutiques du Ministere de l'Industrie
// Pharmaceutique (miph.gov.dz — source publique, independante de
// PharmNet ; voir backend/tools/pharmnet_dictionary/build_dci_names.py).
// Le champ peut etre absent pour un `dci_code` non couvert par cette
// nomenclature (ex. mise a jour du registre PharmNet posterieure au
// fichier ministere) — toujours l'afficher de façon optionnelle, et un
// `dci_code` sans `dci_name` n'entre jamais dans `sameSubstanceGroups`
// (aucune substance a comparer).
//
// Anomalie connue et acceptee (voir build_dci_names.py) : 2 codes sur 2056
// (02 B 067, 27 C 003) couvrent chacun DEUX substances differentes dans la
// nomenclature source elle-meme — le `dci_name` retenu pour ces 2 codes
// est donc parfois errone pour l'une des deux. Risque residuel tres faible
// (2/2056), non corrige manuellement.
const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

const medKey = (m) => m.num_enregistrement || m.external_id || m.nom_commercial;

const DrugDuplicateChecker = () => {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [selected, setSelected] = useState([]);
  const debounceRef = useRef(null);

  const runSearch = useCallback(async (q) => {
    if (q.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      setSearchError(false);
      return;
    }
    setLoading(true);
    setSearchError(false);
    try {
      const res = await axios.get(`${API_URL}/api/medications`, {
        params: { search: q, limit: 20 },
      });
      setResults(res.data?.medications || []);
    } catch {
      setResults([]);
      setSearchError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(val.trim()), SEARCH_DEBOUNCE_MS);
  };

  const toggle = (med) => {
    setSelected((sel) =>
      sel.some((d) => medKey(d) === medKey(med)) ? sel.filter((d) => medKey(d) !== medKey(med)) : [...sel, med]
    );
  };

  const duplicateGroups = useMemo(() => computeDuplicateGroups(selected), [selected]);

  // Niveau 2 (ambre) : meme substance (`dci_name` normalise), mais au
  // moins deux `dci_code` distincts presents -> dosages/formes differents.
  // Si tous les medicaments d'un meme dci_name partagent en realite le
  // meme dci_code, c'est un doublon exact deja couvert par duplicateGroups
  // ci-dessus, pas la peine de le re-signaler ici (voir
  // utils/medicationDuplicates.js pour le detail de la regle).
  const sameSubstanceGroups = useMemo(() => computeSameSubstanceGroups(selected), [selected]);

  return (
    <ToolPageLayout
      title={t('drugdup_title')}
      subtitle={t('drugdup_subtitle')}
      specialty="generaliste"
    >
      <div className="bg-[#E6F4FB] border border-[#0596DE]/20 rounded-2xl p-4 flex gap-3 mb-8">
        <Info className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
        <p className="text-sm text-[#0F172A]">
          {t('drugdup_info')}
        </p>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={query}
          onChange={handleQueryChange}
          placeholder={t('drugdup_search_placeholder')}
          className="w-full border border-[#E2E8F0] rounded-xl pl-9 pr-3 py-2 text-sm bg-white h-11 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-2 mb-8 max-h-72 overflow-y-auto border border-[#E2E8F0] rounded-2xl p-3">
        {query.trim().length < MIN_QUERY_LENGTH ? (
          <p className="text-sm text-[#94A3B8] col-span-2 text-center py-4">{t('drugdup_type_more')}</p>
        ) : loading ? (
          <p className="text-sm text-[#94A3B8] col-span-2 text-center py-4">{t('drugdup_searching')}</p>
        ) : searchError ? (
          <p className="text-sm text-[#EF4444] col-span-2 text-center py-4">{t('drugdup_search_error')}</p>
        ) : results.length === 0 ? (
          <p className="text-sm text-[#94A3B8] col-span-2 text-center py-4">{t('drugdup_none_found')}</p>
        ) : (
          results.map((d) => {
            const key = medKey(d);
            const isSelected = selected.some((s) => medKey(s) === key);
            const subtitle = [d.dci_name, d.dosage, d.forme].filter(Boolean).join(' — ');
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggle(d)}
                className={`text-left rounded-xl px-3 py-2 text-sm border transition-colors ${isSelected ? 'bg-[#0596DE] text-white border-[#0596DE]' : 'bg-white text-[#0F172A] border-[#E2E8F0] hover:border-[#0596DE]/40'}`}
              >
                <div className="font-medium">{d.nom_commercial}</div>
                {subtitle && (
                  <div className={`text-xs ${isSelected ? 'text-white/80' : 'text-[#64748B]'}`}>{subtitle}</div>
                )}
              </button>
            );
          })
        )}
      </div>

      {selected.length > 0 && (
        <div className="mb-8">
          <h3 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
            <PillBottle className="w-5 h-5 text-[#0596DE]" /> {t('drugdup_selected_heading')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {selected.map((d) => {
              // Deux medicaments peuvent partager le meme nom_commercial (ex.
              // "AMOXICILLINE - SOLVAY" en 1G et en 500MG, ce sont deux
              // enregistrements distincts) : sans le dosage/la forme ici,
              // rien ne les distingue visuellement dans cette liste, ce qui
              // peut laisser croire a tort qu'on a selectionne deux fois le
              // meme produit (ou l'inverse). Toujours afficher le detail
              // quand il existe.
              const detail = [d.dosage, d.forme].filter(Boolean).join(' — ');
              return (
                <span key={medKey(d)} className="inline-flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full px-3 py-1.5 text-sm">
                  <span>
                    {d.nom_commercial}
                    {detail && <span className="text-[#64748B] text-xs ml-1">({detail})</span>}
                  </span>
                  <button type="button" onClick={() => toggle(d)}>
                    <X className="w-3.5 h-3.5 text-[#94A3B8]" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {duplicateGroups.length > 0 || sameSubstanceGroups.length > 0 ? (
        <div className="space-y-3">
          {duplicateGroups.map(([dciCode, drugs]) => {
            const dciName = drugs.find((d) => d.dci_name)?.dci_name;
            return (
              <div key={`exact-${dciCode}`} className="bg-[#FEE2E2] border border-[#EF4444]/30 rounded-2xl p-4 flex gap-3">
                <AlertTriangle className="w-5 h-5 text-[#EF4444] shrink-0 mt-0.5" />
                <div className="text-sm text-[#991B1B]">
                  <p>
                    <strong>{drugs.map((d) => d.nom_commercial).join(t('drugdup_and'))}</strong>{t('drugdup_dup_mid')}{t('drugdup_dup_post')}
                  </p>
                  {dciName && (
                    <p className="text-xs text-[#991B1B]/70 mt-1">{t('drugdup_substance_label')}{dciName}</p>
                  )}
                </div>
              </div>
            );
          })}
          {sameSubstanceGroups.map(([groupKey, drugs, displayName]) => (
            <div key={`substance-${groupKey}`} className="bg-[#FFFBEB] border border-[#F59E0B]/30 rounded-2xl p-4 flex gap-3">
              <AlertTriangle className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" />
              <p className="text-sm text-[#92400E]">
                <strong>
                  {drugs
                    .map((d) => `${d.nom_commercial} (${[d.dosage, d.forme].filter(Boolean).join(' — ')})`)
                    .join(t('drugdup_and'))}
                </strong>
                {t('drugdup_samesubstance_mid')}<strong>{displayName}</strong>{t('drugdup_samesubstance_post')}
              </p>
            </div>
          ))}
        </div>
      ) : selected.length >= 2 ? (
        <div className="bg-[#F0FDF4] border border-[#10B981]/30 rounded-2xl p-4">
          <p className="text-sm text-[#065F46]">{t('drugdup_no_duplicate')}</p>
        </div>
      ) : null}

      <p className="mt-8 text-sm text-[#64748B]">
        {t('drugdup_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default DrugDuplicateChecker;
